import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { API, apiFetch } from '../lib/api';

const SecurityContext = createContext(null);

export const SecurityProvider = ({ children }) => {
  const [isVerified, setIsVerified]     = useState(false);
  const [loading, setLoading]           = useState(true);
  const [fingerprint, setFingerprint]   = useState('');
  const [guardStatus, setGuardStatus]   = useState(null);
  const [mssqlConnected, setMssqlConn]  = useState(false);
  const [chainHeight, setChainHeight]   = useState(0);

  const verify = useCallback(async () => {
    try {
      const res  = await apiFetch(`${API}/health`);
      const data = await res.json();
      if (data.status === 'OK') {
        setIsVerified(true);
        setFingerprint(data.fingerprint || '');
        setGuardStatus(data.guardStatus || null);
        setMssqlConn(data.mssqlConnected || false);
        setChainHeight(data.chainHeight || 0);
      } else {
        setIsVerified(false);
        setGuardStatus(data.guardStatus || { reason: data.reason || 'LOCKED' });
      }
    } catch (e) {
      console.error('[SecurityContext] Health check failed:', e.message);
      setIsVerified(false);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line
  }, []); // BACKEND is a build-time constant; all state setters are stable

  useEffect(() => { verify(); }, [verify]);

  // Memoize context value — prevents children from re-rendering on every SecurityProvider render
  const contextValue = useMemo(() => ({
    isVerified, fingerprint, guardStatus, mssqlConnected, chainHeight
  }), [isVerified, fingerprint, guardStatus, mssqlConnected, chainHeight]);

  if (loading) {
    return (
      <div style={{ minHeight:'100vh', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ textAlign:'center', color:'#94a3b8' }}>
          <div style={{ width:40, height:40, border:'3px solid #3b82f6', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 1s linear infinite', margin:'0 auto 16px' }} />
          <p style={{ fontFamily:'monospace', fontSize:12, letterSpacing:2 }}>VALIDATING DNA SIGNATURE...</p>
        </div>
      </div>
    );
  }

  if (!isVerified) {
    return (
      <div style={{ minHeight:'100vh', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center', padding:32 }}>
        <div style={{ maxWidth:480, background:'#1e293b', border:'2px solid #ef4444', borderRadius:16, padding:40, textAlign:'center', boxShadow:'0 25px 50px rgba(0,0,0,0.5)' }}>
          <div style={{ fontSize:48, marginBottom:16 }}>⚠️</div>
          <h1 style={{ color:'#f8fafc', fontWeight:900, fontSize:24, marginBottom:12 }}>SECURITY ALERT</h1>
          <p style={{ color:'#94a3b8', marginBottom:24, lineHeight:1.6 }}>Application Identity verification failed. This session is unauthorized.</p>
          <div style={{ background:'#0f172a', padding:16, borderRadius:8, textAlign:'left', fontFamily:'monospace', fontSize:11, color:'#ef4444', marginBottom:24 }}>
            ERROR: IDENTITY_CHAIN_BROKEN<br/>
            OWNER: MSN-CLIQ<br/>
            CODE: {guardStatus?.reason || 'IP_VIOLATION_911'}
          </div>
          <button onClick={() => { setLoading(true); verify(); }}
            style={{ background:'#4f46e5', color:'#fff', fontWeight:700, padding:'12px 32px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, letterSpacing:1 }}>
            RETRY CONNECTION
          </button>
        </div>
      </div>
    );
  }

  return (
    <SecurityContext.Provider value={contextValue}>
      {children}
    </SecurityContext.Provider>
  );
};

export const useSecurity = () => {
  const ctx = useContext(SecurityContext);
  if (!ctx) throw new Error('useSecurity must be inside SecurityProvider');
  return ctx;
};
